import { useEffect, type ReactElement } from "react";
import { useParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";

import { LoadingPage } from "./loading-page.js";
import { tenantApi, tenantUrls } from "./urls.js";

/**
 * 完了画面に出すもの（TenantCompletedViewModel）。<b>題名はサーバが決める。</b>
 * `initialStorageSize` は API が返すが画面には出さない（2026-10-10 に利用者が決めた。初期容量は申し込みの画面で見せている）。
 */
export interface TenantCompleted {
    readonly tenantId: number;
    readonly initialStorageSize: number;
    readonly currentServiceId: string;
    readonly isPlanChange: boolean;
    readonly title: string;
}

function CompletedView({ value }: { readonly value: TenantCompleted }): ReactElement {
    usePageMeta(value.title);
    // 申し込みの完了を 1 つのイベントとして計測する（GA4。本番だけ読み込まれる。個人の情報は送らない）
    useEffect(() => {
        if (!value.isPlanChange) {
            (globalThis as { gtag?: (...args: unknown[]) => void }).gtag?.("event", "portal_signup_completed");
        }
    }, [value.isPlanChange]);

    return (
        <div className="page max-w-lg">
            <div className="panel-message">
                <h1 className="page-title">{value.title}</h1>
                {value.isPlanChange
                    ? (
                        <p className="page-lead">
                            ご契約内容の変更が完了しました。<br />
                            ご登録のメールアドレス宛に、変更後の内容をお送りしています。
                        </p>
                    )
                    : (
                        <p className="page-lead">
                            お申し込みが完了しました。<br />
                            ご登録のメールアドレス宛に、お申し込み内容をお送りしています。
                        </p>
                    )}
                <div className="flex flex-col justify-center gap-3 sm:flex-row">
                    <a
                        className="btn-primary" href={tenantUrls.service(value.currentServiceId)}
                        target="_blank" rel="noopener noreferrer"
                    >
                        サービスにログインする
                    </a>
                    <a className="btn-secondary" href={tenantUrls.list}>契約一覧へ</a>
                </div>
            </div>
        </div>
    );
}

/**
 * 申し込み・プラン変更の完了。<b>読み込み直しても出せるよう、URL の契約から引き直す</b>
 * （今のアプリは確定の応答にそのまま描いていた）。
 */
function Completed({ kind }: { readonly kind: "Provisioned" | "PlanChanged" }): ReactElement {
    const tenantId = useParams()["tenantId"] ?? "";
    const data = useApiData<TenantCompleted>(tenantApi.completed(tenantId, kind));

    return data.state === "ready"
        ? <CompletedView value={data.value} />
        : <LoadingPage title="お手続きの完了" data={data} width="max-w-lg" />;
}

export function SignupCompletedPage(): ReactElement {
    return <Completed kind="Provisioned" />;
}

export function PlanChangedPage(): ReactElement {
    return <Completed kind="PlanChanged" />;
}
